/**
 * Shared visual tokens for booking availability statuses across
 * appointment calendar overlay, schedule panel time buttons, and doctor select.
 */
export type BookingVisualStatus =
  | 'available'
  | 'working'
  | 'warn'
  | 'blocked'
  | 'busy'
  | 'not_working';

export type BookingSlotCellStatus = 'available' | 'warn' | 'blocked' | 'busy' | 'not_working';

export interface BookingStatusVisual {
  /** i18n key under appointment:bookingStatus.* */
  labelKey: string;
  /** Soft background for calendar cells */
  bgcolor: string;
  /** Border / accent */
  borderColor: string;
  /** Whether the slot/option is selectable */
  selectable: boolean;
  /** Button intent for time chips when applicable */
  buttonIntent?: 'primary' | 'secondary' | 'warning';
  /** Optional pattern (e.g. outside working hours) while keeping a white base */
  backgroundImage?: string;
}

/** Same hatch as ngoài ca, so busy intervals read as unavailable on the grid. */
export const UNAVAILABLE_STRIPE =
  'repeating-linear-gradient(135deg, transparent 0 3px, rgba(0,0,0,0.07) 3px 5px)';

const VISUALS: Record<BookingVisualStatus, BookingStatusVisual> = {
  available: {
    labelKey: 'bookingStatus.available',
    bgcolor: 'common.white',
    borderColor: 'divider',
    selectable: true,
    buttonIntent: 'secondary',
  },
  working: {
    labelKey: 'bookingStatus.working',
    bgcolor: 'common.white',
    borderColor: 'divider',
    selectable: true,
    buttonIntent: 'secondary',
  },
  warn: {
    labelKey: 'bookingStatus.warn',
    bgcolor: 'warning.50',
    borderColor: 'warning.light',
    selectable: true,
    buttonIntent: 'warning',
  },
  blocked: {
    labelKey: 'bookingStatus.blocked',
    bgcolor: 'action.disabledBackground',
    borderColor: 'divider',
    selectable: false,
  },
  busy: {
    labelKey: 'bookingStatus.busy',
    bgcolor: 'common.white',
    borderColor: 'grey.400',
    selectable: false,
    backgroundImage: UNAVAILABLE_STRIPE,
  },
  not_working: {
    labelKey: 'bookingStatus.notWorking',
    bgcolor: 'common.white',
    borderColor: 'grey.300',
    selectable: false,
    backgroundImage: UNAVAILABLE_STRIPE,
  },
};

export const getBookingStatusVisual = (
  status: BookingVisualStatus | undefined | null
): BookingStatusVisual => {
  if (!status) return VISUALS.available;
  return VISUALS[status] ?? VISUALS.available;
};

/**
 * Resolve overlay status for a grid cell given doctor-day matrix data.
 * Missing slot labels outside working windows → not_working.
 */
export const resolveSlotCellStatus = (params: {
  isWorking: boolean | undefined;
  slotStatus?: 'available' | 'warn' | 'blocked' | 'busy';
  hasSlotEntry: boolean;
}): BookingSlotCellStatus => {
  if (params.slotStatus === 'busy') return 'busy';
  if (params.isWorking === false) return 'not_working';
  if (!params.hasSlotEntry) return 'not_working';
  if (params.slotStatus === 'warn') return 'warn';
  if (params.slotStatus === 'blocked') return 'blocked';
  return 'available';
};
